// ----------------------------------------------------------------------- imports externos
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

/* Un bloque de una ficha, con sus cuatro estados.
 *
 * ── POR QUÉ ES UN COMPONENTE Y NO UNA COPIA EN CADA FICHA ────────────────
 *
 * Nació adentro de la ficha de la unidad y la de la persona lo necesitaba
 * igual. Copiado, serían **dos definiciones de cómo se dice «no se pudo
 * consultar»** — y el día que una cambie, las dos fichas contestarían distinto
 * sobre la misma clase de problema. Es la misma razón por la que `Ubicacion`,
 * `ChipUsuario` y `VeredictoRTO` salieron de adentro de sus pantallas.
 *
 * ── CUATRO ESTADOS Y NO DOS ──────────────────────────────────────────────
 *
 *   cargando · listo · vacío · no se pudo
 *
 * «No hay tickets» y «no pude consultar la ticketera» se ven igual si se
 * dibujan igual, y mandan a hacer cosas opuestas: el primero es un camión sin
 * trabajo pendiente y el segundo es un problema del sistema. Alguien sale a
 * arreglar el que no está roto.
 *
 * ── Y EL TÍTULO SE DIBUJA SIEMPRE… SALVO QUE SE PIDA LO CONTRARIO ────────
 *
 * Incluso sin dato. Un bloque que desaparece hace que «esta unidad no tiene
 * tickets» y «esta pantalla no mira el taller» se vean igual. Es lo que hacen
 * Diferencias y la tabla de unidades, y no cambia.
 *
 * Las dos fichas pidieron lo otro. Igna, 02/10: *«si están en "0" o vacías,
 * que no aparezcan»*. En la persona son dieciséis bloques y en la que no
 * maneja la mitad decía «no hay», ocupando lo mismo que los que tienen algo:
 * había que leerlos todos para saber cuáles decían algo. Y el mismo día, de
 * la unidad: *«lo mismo para los vehículos»*. Por eso `ocultarSiVacio` es
 * OPT-IN y lo prenden sólo esas dos fichas.
 *
 * Lo que se esconde es el VACÍO y nada más. «Consultando…» y «no se pudo
 * consultar» se siguen viendo: esconder una falla sería volver a dibujarla
 * igual que un «no hay», que es justo lo que este componente vino a evitar.
 *
 * ── EL VACÍO LO PUEDE SABER LA PÁGINA, O SÓLO EL HIJO ────────────────────
 *
 * Cuando lo sabe la página, lo dice con `vacio` (el texto de siempre). Cuando
 * lo sabe sólo el componente de adentro —`DondeEsta`, `TractorAsignado`,
 * `Telefono`, `ChoferesDelTractor`… salen a la red por su cuenta—, el hijo avisa con
 * `useAvisarVacio(true|false)`. Por eso el bloque escondido NO DESMONTA a sus
 * hijos, se dibuja con `hidden`: desmontado, el hijo no podría avisar el día
 * que su dato llegue, y el bloque quedaría escondido para siempre.
 *
 * `comoLlegar` es la salida a la pantalla donde ese dato SÍ se carga. Va en el
 * encabezado y no abajo: la ficha es de lectura, y el que llega buscando dónde
 * cargar tiene que verlo sin recorrer el bloque entero.
 *
 * `alerta` lo pinta en ámbar suave: hay algo para mirar adentro, y las dos
 * fichas además lo nombran arriba de todo.
 */

/* Por acá el hijo le dice a su bloque «no tengo nada». Afuera de un bloque
 * —o en uno sin `ocultarSiVacio`— el valor es `null` y avisar no hace nada,
 * así que los mismos componentes siguen andando en el panel del padrón y en
 * el tablero de herramientas sin enterarse. */
const AvisarVacio = createContext(null);

/* Y por acá el bloque le dice a su tema si se ve. Ver `TemaFicha`. */
const RegistroDelTema = createContext(null);

/* El hijo avisa si quedó vacío.
 *
 *   true       llegó, y no hay nada → el bloque se esconde
 *   false      hay algo, o no se pudo mirar → se ve
 *   undefined  todavía no se sabe → se ve (con su «Consultando…» si lo tiene)
 *
 * `useLayoutEffect` y no `useEffect`: el aviso tiene que llegar antes de que
 * se pinte, si no el bloque vacío aparece un cuadro y se va, que se lee como
 * que la pantalla se corrigió sola. */
export function useAvisarVacio(estaVacio) {
  const avisar = useContext(AvisarVacio);
  useLayoutEffect(() => {
    if (!avisar) return undefined;
    avisar(estaVacio);
    return () => avisar(undefined);
  }, [avisar, estaVacio]);
}

const BloqueFicha = ({
  titulo,
  estado,
  children,
  vacio,
  comoLlegar,
  ocultarSiVacio = false,
  alerta = false,
}) => {
  const [hijoVacio, setHijoVacio] = useState(undefined);
  const tema = useContext(RegistroDelTema);
  const clave = useId();

  const oculto =
    ocultarSiVacio && estado === "listo" && (Boolean(vacio) || hijoVacio === true);

  /* El tema necesita saber si queda alguno a la vista para dibujar su título.
   * Al desmontarse, el bloque se borra del registro: uno que ya no está no
   * puede seguir sosteniendo un título. */
  useLayoutEffect(() => {
    if (!tema) return undefined;
    tema.avisar(clave, !oculto);
    return undefined;
  }, [tema, clave, oculto]);
  useLayoutEffect(() => {
    if (!tema) return undefined;
    return () => tema.sacar(clave);
  }, [tema, clave]);

  /* Adentro de un tema el título baja un nivel: arriba está el nombre de la
   * persona (h2) y el del tema (h3). */
  const Titulo = tema ? "h4" : "h3";

  return (
    <section
      className={"pf-bloque" + (alerta ? " pf-bloque--alerta" : "")}
      hidden={oculto || undefined}
    >
      {/* Escondido, sin título: el texto quedaría en la página aunque no se
          vea, y el que busca «Vacaciones» con el buscador del navegador
          encontraría un bloque que no está. */}
      {!oculto && (
        <div className="pf-bloque-cab">
          <Titulo>{titulo}</Titulo>
          {comoLlegar}
        </div>
      )}
      {estado === "cargando" && <p className="tenue">Consultando…</p>}
      {estado === "error" && (
        <p className="pf-nopude">No se pudo consultar.</p>
      )}
      {estado === "listo" &&
        (vacio ? (
          !oculto && <p className="tenue">{vacio}</p>
        ) : (
          <AvisarVacio.Provider value={ocultarSiVacio ? setHijoVacio : null}>
            {children}
          </AvisarVacio.Provider>
        ))}
    </section>
  );
};

/* Un tema de la ficha: un recuadro con filo de color, ícono y título, y sus
 * bloques en grilla adentro.
 *
 * ── SI NO QUEDA NINGÚN BLOQUE A LA VISTA, EL TEMA NO SE DIBUJA ───────────
 *
 * Igna, 02/10, sobre la ficha de la persona y después la de la unidad: lo
 * vacío no aparece. Un título
 * «Contacto» sobre un recuadro sin nada sería el mismo hueco de antes, más
 * grande.
 *
 * SE RESUELVE CON ESTADO Y NO CON CSS `:has()`. Con `:has()` alcanzaba una
 * regla —«el tema que no tiene un bloque sin `hidden`, no se ve»— pero el
 * título quedaría en la página, y lo que no se puede probar en un test (jsdom
 * no aplica hojas de estilo) tarde o temprano se rompe sin que nadie se
 * entere. Así, cada bloque le avisa al tema si se ve y el tema sabe contar.
 *
 * El recuadro escondido sigue MONTADO, con sus bloques adentro, por lo mismo
 * que el bloque escondido: un hijo que todavía está consultando tiene que
 * poder aparecer cuando llegue su dato. Y sin ningún bloque registrado
 * —todos condicionados por permiso, o por no ser chofer— también se esconde.
 */
export const TemaFicha = ({ id, titulo, icono, children }) => {
  const [visibles, setVisibles] = useState({});

  const avisar = useCallback(
    (clave, visible) =>
      setVisibles((v) => (v[clave] === visible ? v : { ...v, [clave]: visible })),
    [],
  );
  const sacar = useCallback(
    (clave) =>
      setVisibles((v) => {
        if (!(clave in v)) return v;
        const resto = { ...v };
        delete resto[clave];
        return resto;
      }),
    [],
  );
  const registro = useMemo(() => ({ avisar, sacar }), [avisar, sacar]);

  const hay = Object.values(visibles).some(Boolean);
  const cuantos = Object.values(visibles).filter(Boolean).length;
  const idTitulo = `pf-tema-${id}`;

  /* `--pf-n` = cuántos bloques se ven. Con eso el recuadro pide el ancho de
     sus columnas y los temas se acomodan uno al lado del otro (ver
     `.pf-temas` en fichas-pantalla.css). */
  return (
    <section
      className={`pf-tema pf-tema--${id}`}
      hidden={!hay || undefined}
      aria-labelledby={hay ? idTitulo : undefined}
      style={hay ? { "--pf-n": cuantos } : undefined}
    >
      {hay && (
        <h3 className="pf-tema-tit" id={idTitulo}>
          {icono}
          {titulo}
        </h3>
      )}
      <div className="pf-tema-grilla">
        <RegistroDelTema.Provider value={registro}>
          {children}
        </RegistroDelTema.Provider>
      </div>
    </section>
  );
};

export default BloqueFicha;
